import { supabase } from "src/lib/supabaseClient";

export interface EnrollmentProduct {
  id: string;
  name: string;
  price: number;
  duration_days: number;
  type: "booster" | "custom" | "preparation";
  subject_id?: string | null;
}

export interface EnrollmentStudent {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
}

export interface ManualEnrollmentDraftInput {
  studentId: string;
  productId: string;
  curriculumScopeId: string;
  selectionTargets: CurriculumSelectionTarget[];
  examBlueprintId?: string;
  prerequisiteWaivers?: PrerequisiteWaiver[];
  totalSessions: number;
  actualPricePerSession: number;
  overrideReason?: string;
  paymentKind: "full" | "installment";
  installmentAmount?: number;
  currency: string;
  paymentMethod: string;
  paymentReference: string;
  paidAt: string;
  verificationNotes: string;
}

export type CurriculumTargetType = "domain" | "unit" | "lesson" | "objective";

export interface CurriculumSelectionTarget {
  type: CurriculumTargetType;
  id: string;
}

export interface PrerequisiteWaiver {
  objective_id: string;
  prerequisite_objective_id: string;
  reason: string;
}

export interface EnrollmentCurriculumScope {
  id: string;
  subject_id: string;
  board_id: string | null;
  grade_id: string | null;
  version_label: string;
  boards: { board_name: string } | null;
  grades: { grade_label: string } | null;
}

export interface EnrollmentCurriculumTarget extends CurriculumSelectionTarget {
  label: string;
  detail: string;
}

export interface EnrollmentExamBlueprint {
  id: string;
  name: string;
  version_label: string;
}

export interface ManualEnrollmentDraft {
  workflow_id: string;
  invoice_id: string;
}

export interface ManualEnrollmentRecord {
  workflow_id: string;
  invoice_id: string;
  subscription_id: string | null;
  student_id: string;
  student_name: string;
  student_email: string;
  product_id: string;
  product_name: string;
  product_type: string;
  amount: number;
  currency: string;
  invoice_pdf_url: string | null;
  enrollment_state: "awaiting_invoice_upload" | "ready_to_finalize" | "enrolled" | "cancelled" | "failed";
  access_expires_at: string | null;
  has_batch: boolean;
  created_at: string;
  sessions_count: number | null;
  default_price_per_session: number | null;
  actual_price_per_session: number | null;
  total_purchase_price: number | null;
  actual_purchase_price: number | null;
  total_amount_received: number;
  amount_due: number;
  cancellation_reason: string | null;
  curriculum_scope: EnrollmentCurriculumScope | null;
}

export interface EnrollmentBatchCapacity {
  id: string;
  name: string;
  total_sessions: number;
  remaining_sessions: number;
}

export interface BatchExtensionContext extends EnrollmentBatchCapacity {
  student_id: string;
  subscription_id: string;
  currency: string;
  actual_price_per_session: number | null;
}

export interface SessionExtensionRecord {
  id: string;
  additional_sessions: number;
  actual_price_per_session: number;
  extension_amount: number;
  amount_received: number;
  currency: string;
  payment_reference: string;
  invoice_pdf_url: string;
  created_at: string;
}

export interface AdminSessionExtensionInput {
  batchId: string;
  studentId: string;
  additionalSessions: number;
  actualPricePerSession: number;
  amountReceived: number;
  currency: string;
  paymentMethod: string;
  paymentReference: string;
  paidAt: string;
  verificationNotes: string;
  invoiceFile: File;
}

type SupabaseRequestError = {
  message: string;
  details?: string | null;
  hint?: string | null;
  code?: string | null;
};

const formatRequestError = (error: SupabaseRequestError | null): string => {
  if (!error) return "The enrollment request returned no data";
  // PostgREST often puts the actionable database exception in `message`, but
  // preserve a non-duplicate detail/hint when it provides more context.
  const context = [error.details, error.hint]
    .filter((value): value is string => Boolean(value && value !== error.message))
    .join(" ");
  return context ? `${error.message} ${context}` : error.message;
};

const requireData = <T>(data: T | null, error: SupabaseRequestError | null): T => {
  if (error || !data) throw new Error(formatRequestError(error));
  return data;
};

export const manualEnrollmentService = {
  async fetchStudents(): Promise<EnrollmentStudent[]> {
    const { data, error } = await supabase.rpc("get_all_users");
    if (error) throw new Error(error.message);
    return (data || [])
      .filter((user: { role?: string | null }) => user.role === "student")
      .map((user: EnrollmentStudent) => user);
  },

  async fetchProducts(): Promise<EnrollmentProduct[]> {
    const { data, error } = await supabase
      .from("products")
      .select("id, name, price, duration_days, type, subject_id")
      .eq("is_active", true)
      .order("name");
    if (error) throw new Error(error.message);
    return (data || []) as EnrollmentProduct[];
  },

  async fetchPublishedScopes(subjectId?: string | null): Promise<EnrollmentCurriculumScope[]> {
    if (!subjectId) return [];
    const { data, error } = await supabase
      .from("curriculum_scopes")
      .select("id, subject_id, board_id, grade_id, version_label, boards(board_name), grades(grade_label)")
      .eq("subject_id", subjectId)
      .eq("status", "published")
      .order("version_label");
    if (error) throw new Error(error.message);
    return (data || []) as EnrollmentCurriculumScope[];
  },

  async fetchScopeTargets(scopeId: string): Promise<EnrollmentCurriculumTarget[]> {
    const { data: scoped, error: scopedError } = await supabase
      .from("curriculum_scope_objectives")
      .select("objective_id")
      .eq("scope_id", scopeId)
      .order("display_order");
    if (scopedError) throw new Error(scopedError.message);
    const objectiveIds = (scoped || []).map((row: { objective_id: string }) => row.objective_id);
    if (!objectiveIds.length) return [];

    const { data: objectives, error: objectivesError } = await supabase
      .from("curriculum_objectives")
      .select("id, title, unit_id, lesson_id")
      .in("id", objectiveIds)
      .eq("status", "published");
    if (objectivesError) throw new Error(objectivesError.message);
    const unitIds = [...new Set((objectives || []).map((item: { unit_id: string }) => item.unit_id))];
    const lessonIds = [...new Set((objectives || []).map((item: { lesson_id: string | null }) => item.lesson_id).filter(Boolean))] as string[];
    const [{ data: units, error: unitsError }, { data: lessons, error: lessonsError }] = await Promise.all([
      supabase.from("curriculum_units").select("id, name, domain_id").in("id", unitIds).eq("status", "published"),
      lessonIds.length ? supabase.from("curriculum_lessons").select("id, name, unit_id").in("id", lessonIds).eq("status", "published") : Promise.resolve({ data: [], error: null }),
    ]);
    if (unitsError) throw new Error(unitsError.message);
    if (lessonsError) throw new Error(lessonsError.message);
    const domainIds = [...new Set((units || []).map((item: { domain_id: string }) => item.domain_id))];
    const { data: domains, error: domainsError } = await supabase.from("curriculum_domains").select("id, name").in("id", domainIds).eq("status", "published");
    if (domainsError) throw new Error(domainsError.message);
    const unitById = new Map((units || []).map((item: { id: string; name: string; domain_id: string }) => [item.id, item]));
    const lessonById = new Map((lessons || []).map((item: { id: string; name: string; unit_id: string }) => [item.id, item]));
    const domainById = new Map((domains || []).map((item: { id: string; name: string }) => [item.id, item]));
    const targets = new Map<string, EnrollmentCurriculumTarget>();
    for (const objective of objectives || []) {
      const unit = unitById.get(objective.unit_id); const domain = unit && domainById.get(unit.domain_id); const lesson = objective.lesson_id ? lessonById.get(objective.lesson_id) : undefined;
      if (domain) targets.set(`domain:${domain.id}`, { type: "domain", id: domain.id, label: domain.name, detail: "Domain" });
      if (unit) targets.set(`unit:${unit.id}`, { type: "unit", id: unit.id, label: unit.name, detail: domain ? `Unit · ${domain.name}` : "Unit" });
      if (lesson) targets.set(`lesson:${lesson.id}`, { type: "lesson", id: lesson.id, label: lesson.name, detail: unit ? `Lesson · ${unit.name}` : "Lesson" });
      targets.set(`objective:${objective.id}`, { type: "objective", id: objective.id, label: objective.title, detail: lesson ? `Objective · ${lesson.name}` : "Objective" });
    }
    return [...targets.values()];
  },

  async fetchProductExamBlueprints(productId: string): Promise<EnrollmentExamBlueprint[]> {
    const { data, error } = await supabase
      .from("product_exam_blueprints")
      .select("exam_blueprints(id, name, version_label, status)")
      .eq("product_id", productId);
    if (error) throw new Error(error.message);
    return (data || []).flatMap((row: { exam_blueprints: EnrollmentExamBlueprint & { status: string } | null }) => row.exam_blueprints?.status === "published" ? [{ id: row.exam_blueprints.id, name: row.exam_blueprints.name, version_label: row.exam_blueprints.version_label }] : []);
  },

  async createDraft(input: ManualEnrollmentDraftInput): Promise<ManualEnrollmentDraft> {
    const { data, error } = await supabase.rpc("create_manual_enrollment_draft", {
      p_student_id: input.studentId,
      p_product_id: input.productId,
      p_curriculum_scope_id: input.curriculumScopeId,
      p_selection_targets: input.selectionTargets,
      p_exam_blueprint_id: input.examBlueprintId || null,
      p_prerequisite_waivers: input.prerequisiteWaivers || [],
      p_total_sessions: input.totalSessions,
      p_actual_price_per_session: input.actualPricePerSession,
      p_override_reason: input.overrideReason || null,
      p_payment_kind: input.paymentKind,
      p_installment_amount: input.installmentAmount || null,
      p_currency: input.currency.toLowerCase(),
      p_payment_method: input.paymentMethod,
      p_payment_reference: input.paymentReference,
      p_paid_at: input.paidAt,
      p_verification_notes: input.verificationNotes,
    });
    const result = requireData((data || [])[0] as ManualEnrollmentDraft | undefined, error);
    return result;
  },

  async uploadInvoicePdf(studentId: string, invoiceId: string, file: File): Promise<string> {
    if (file.type !== "application/pdf" || file.size > 10 * 1024 * 1024) {
      throw new Error("Upload a PDF invoice smaller than 10 MB.");
    }
    const path = `${studentId}/${invoiceId}/${crypto.randomUUID()}.pdf`;
    const { error } = await supabase.storage.from("invoices").upload(path, file, {
      contentType: "application/pdf",
      upsert: false,
    });
    if (error) throw new Error(error.message);
    return path;
  },

  async finalize(workflowId: string, invoiceId: string, invoicePdfPath: string): Promise<string> {
    const { data, error } = await supabase.rpc("finalize_manual_enrollment", {
      p_workflow_id: workflowId,
      p_invoice_id: invoiceId,
      p_invoice_pdf_path: invoicePdfPath,
    });
    return requireData(data as string | null, error);
  },

  async list(): Promise<ManualEnrollmentRecord[]> {
    const { data, error } = await supabase.rpc("admin_list_manual_enrollments");
    if (error) throw new Error(error.message);
    const records = (data || []) as ManualEnrollmentRecord[];
    if (!records.length) return records;

    const workflowIds = records.map((record) => record.workflow_id);
    const { data: plans, error: plansError } = await supabase
      .from("subscription_curriculum")
      .select("workflow_id, curriculum_scope_id")
      .in("workflow_id", workflowIds);
    if (plansError) throw new Error(plansError.message);

    const scopeIdByWorkflow = new Map(
      (plans || [])
        .filter((plan: { workflow_id: string | null; curriculum_scope_id: string | null }) => plan.workflow_id && plan.curriculum_scope_id)
        .map((plan: { workflow_id: string; curriculum_scope_id: string }) => [plan.workflow_id, plan.curriculum_scope_id])
    );
    const scopeIds = [...new Set(scopeIdByWorkflow.values())];
    const scopeById = new Map<string, EnrollmentCurriculumScope>();
    if (scopeIds.length) {
      const { data: scopes, error: scopesError } = await supabase
        .from("curriculum_scopes")
        .select("id, subject_id, board_id, grade_id, version_label, boards(board_name), grades(grade_label)")
        .in("id", scopeIds);
      if (scopesError) throw new Error(scopesError.message);
      for (const scope of (scopes || []) as EnrollmentCurriculumScope[]) scopeById.set(scope.id, scope);
    }
    const subscriptionIds = records
      .map((record) => record.subscription_id)
      .filter((id): id is string => Boolean(id));
    if (!subscriptionIds.length) return records.map((record) => ({
      ...record,
      curriculum_scope: scopeById.get(scopeIdByWorkflow.get(record.workflow_id) || "") || null,
    }));

    const { data: lines, error: linesError } = await supabase
      .from("session_entitlement_lines")
      .select("subscription_id, session_count")
      .in("subscription_id", subscriptionIds)
      .eq("status", "approved");
    if (linesError) throw new Error(linesError.message);

    const entitledSessions = new Map<string, number>();
    for (const line of lines || []) {
      entitledSessions.set(
        line.subscription_id,
        (entitledSessions.get(line.subscription_id) || 0) + line.session_count
      );
    }
    return records.map((record) => ({
      ...record,
      curriculum_scope: scopeById.get(scopeIdByWorkflow.get(record.workflow_id) || "") || null,
      sessions_count: record.subscription_id
        ? entitledSessions.get(record.subscription_id) ?? record.sessions_count
        : record.sessions_count,
    }));
  },

  async cancel(workflowId: string): Promise<void> {
    const { data, error } = await supabase.rpc("cancel_manual_enrollment_draft", { p_workflow_id: workflowId });
    if (error || !data) throw new Error(error?.message || "Unable to cancel enrollment draft");
  },

  async cancelEnrollment(subscriptionId: string, reason?: string): Promise<void> {
    const { data, error } = await supabase.rpc("admin_cancel_enrollment", {
      p_subscription_id: subscriptionId,
      p_reason: reason || null,
    });
    if (error || !data) throw new Error(error?.message || "Unable to cancel enrollment");
  },

  async fetchBatchCapacity(subscriptionId: string): Promise<EnrollmentBatchCapacity | null> {
    const { data, error } = await supabase
      .from("batches")
      .select("id, name, total_sessions, remaining_sessions")
      .eq("subscription_id", subscriptionId)
      .eq("status", "active")
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data as EnrollmentBatchCapacity | null;
  },

  async fetchBatchExtensionContext(batchId: string): Promise<BatchExtensionContext> {
    const { data: batch, error: batchError } = await supabase
      .from("batches")
      .select("id, name, student_id, subscription_id, total_sessions, remaining_sessions")
      .eq("id", batchId)
      .eq("status", "active")
      .single();
    if (batchError || !batch?.subscription_id) {
      throw new Error(batchError?.message || "An active batch with an enrollment is required");
    }

    const { data: subscription, error: subscriptionError } = await supabase
      .from("subscriptions")
      .select("workflow_id, currency")
      .eq("id", batch.subscription_id)
      .eq("status", "active")
      .single();
    if (subscriptionError || !subscription) {
      throw new Error(subscriptionError?.message || "The batch enrollment is not active");
    }

    const { data: latestEntitlement, error: entitlementError } = await supabase
      .from("session_entitlement_lines")
      .select("actual_price_per_session")
      .eq("subscription_id", batch.subscription_id)
      .eq("status", "approved")
      .order("approved_at", { ascending: false })
      .order("created_at", { ascending: false })
      .limit(1)
      .single();
    if (entitlementError) throw new Error(entitlementError.message);

    return {
      ...batch,
      subscription_id: batch.subscription_id,
      total_sessions: batch.total_sessions ?? 0,
      remaining_sessions: batch.remaining_sessions ?? 0,
      currency: subscription.currency || "usd",
      actual_price_per_session: latestEntitlement?.actual_price_per_session ?? null,
    };
  },

  async listSessionExtensions(batchId: string): Promise<SessionExtensionRecord[]> {
    const { data, error } = await supabase
      .from("session_entitlement_lines")
      .select("id, additional_sessions:session_count, actual_price_per_session, extension_amount:total_amount, amount_received, currency, payment_reference, invoice_pdf_url, created_at")
      .eq("batch_id", batchId)
      .eq("source_type", "admin_extension")
      .eq("status", "approved")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data || []) as SessionExtensionRecord[];
  },

  async createAdminSessionExtension(input: AdminSessionExtensionInput): Promise<string> {
    if (input.invoiceFile.type !== "application/pdf" || input.invoiceFile.size > 10 * 1024 * 1024) {
      throw new Error("Upload a PDF invoice smaller than 10 MB.");
    }

    const extensionId = crypto.randomUUID();
    const invoicePath = `${input.studentId}/${extensionId}/${crypto.randomUUID()}.pdf`;
    const { error: uploadError } = await supabase.storage.from("invoices").upload(invoicePath, input.invoiceFile, {
      contentType: "application/pdf",
      upsert: false,
    });
    if (uploadError) throw new Error(uploadError.message);

    const { data, error } = await supabase.rpc("admin_create_session_extension", {
      p_extension_id: extensionId,
      p_batch_id: input.batchId,
      p_additional_sessions: input.additionalSessions,
      p_actual_price_per_session: input.actualPricePerSession,
      p_amount_received: input.amountReceived,
      p_currency: input.currency.toLowerCase(),
      p_payment_method: input.paymentMethod,
      p_payment_reference: input.paymentReference,
      p_paid_at: input.paidAt,
      p_verification_notes: input.verificationNotes,
      p_invoice_pdf_path: invoicePath,
    });

    if (error || !data) {
      await supabase.storage.from("invoices").remove([invoicePath]);
      throw new Error(error?.message || "Unable to add sessions");
    }
    return data as string;
  },

  async createInvoiceDownloadUrl(path: string): Promise<string> {
    if (/^https?:\/\//i.test(path)) return path;
    const { data, error } = await supabase.storage.from("invoices").createSignedUrl(path, 300, { download: true });
    return requireData(data?.signedUrl || null, error);
  },
};
